import React, { useState, useEffect, useRef } from 'react'
import { bool, func, shape, string } from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import BrandLoader from '../../components/BrandLoader/BrandLoader'
import { appRoutes } from '../../configuration/constants'
import { validateRoute } from '../../redux/selectors/utils'
import {
  getTransactionReceipt,
  clearTicketNumbers,
  clearReceipts
} from '../../redux/booking/actions'
import {
  selectReceipts,
  selectGetReceiptSuccess,
  selectGetReceiptError
} from '../../redux/booking/selectors'

import {
  checkoutResetAction,
  checkoutReadyAction
} from '../../redux/telephoneBooking/actions'
import { selectTelephoneCheckoutInit } from '../../redux/telephoneBooking/selectors'

import {
  CALLBACK_SUCCESS_CODE,
  CANCEL_TRANSACTION,
  CHECKOUT_DIV_ID,
  CHECKOUT_MODE,
  ERROR_EVENT,
  PAGE_LOADED,
  PAYMENT_COMPLETE
} from './constants'
import modalIds from '../../context/configuration'

const MonerisCheckoutFrameWrapper = ({
  preloadReady,
  ticketNumber,
  orderNo,
  preloadError,
  pageData,
  labels,
  openModal,
  handleRouteChange,
  cancelTransactionRouteTarget,
  width,
  isTelephoneBooking
}) => {
  const dispatch = useDispatch()
  // Note: get receipt success reducer replaces all previous receipts with 1 new receipt
  const receipts = useSelector(selectReceipts)
  const receipt = receipts && Object.values(receipts)?.[0]
  const getReceiptError = useSelector(selectGetReceiptError)
  const getReceiptSuccess = useSelector(selectGetReceiptSuccess)
  const isCheckoutFormInitialized = useSelector(selectTelephoneCheckoutInit)
  // check summary is `routeIsValid` or not
  const summaryRouteCheck = useSelector(state =>
    validateRoute(appRoutes.summary.pathname, state)
  )
  // checkout iframe loading state
  const [checkoutStarted, setCheckoutStarted] = useState(true)
  const [pageLoadError, setPageLoadError] = useState('')
  const sessionId = pageData?.preloadParams?.sessionId
  const ferryId = pageData?.preloadParams?.params?.ferryId

  // cache moneris instance
  const monerisRef = useRef(null)
  // 1. safely create & cache a moneris instance!
  useEffect(() => {
    if (typeof window.monerisCheckout !== 'function')
      return console.warn('!!! NO Moneris library inclueded!') // no moneris js included
    if (monerisRef.current) return // only ONCE

    monerisRef.current = new window.monerisCheckout()
    // open checkout form init flag in case of refresh
    dispatch(checkoutResetAction)
  })

  // ========== moneris callbacks ===========================
  const onCheckoutFrameLoaded = jsonRes => {
    const response = JSON.parse(jsonRes)
    const { ticket, response_code } = response
    if (ticket && ticket === ticketNumber) {
      if (response_code === CALLBACK_SUCCESS_CODE) {
        setPageLoadError('')
      } else {
        setPageLoadError(labels.paymentConfirmationError)
      }
    } else {
      setPageLoadError(labels.paymentConfirmationError)
    }
    setCheckoutStarted(false)
  }

  const onPaymentComplete = jsonRes => {
    const response = JSON.parse(jsonRes)
    const { ticket, response_code } = response

    if (
      ticket &&
      ticket === ticketNumber &&
      response_code === CALLBACK_SUCCESS_CODE
    ) {
      // check payment success or failure
      dispatch(
        getTransactionReceipt({
          params: { ferryId, ticket },
          clientId: process.env.REACT_APP_FLOW_CLIENT_ID,
          sessionId,
          isTelephoneBooking
        })
      )
    } else {
      setPageLoadError(labels.paymentConfirmationError)
    }
  }

  const onCancelTransaction = () => {
    const checkoutInstance = monerisRef.current
    if (checkoutInstance && ticketNumber) {
      checkoutInstance.closeCheckout(ticketNumber)
    }
    openModal({ type: 'cancel-transaction' })
    dispatch(clearTicketNumbers())
    handleRouteChange(cancelTransactionRouteTarget)
  }

  const onErrorEvent = () => {
    setPageLoadError(labels.paymentConfirmationError)
  }
  // ========== end of moneris callback ==========================

  // 2. init checkout object and form
  useEffect(() => {
    const checkoutInstance = monerisRef.current
    if (!checkoutInstance) return // wait for instance
    if (isCheckoutFormInitialized) return // only init once!
    if (pageLoadError) return
    // check ticketNumber to create checkout form
    if (!ticketNumber) return

    checkoutInstance.setMode(CHECKOUT_MODE)
    checkoutInstance.setCheckoutDiv(CHECKOUT_DIV_ID)
    checkoutInstance.setCallback(PAGE_LOADED, onCheckoutFrameLoaded)
    checkoutInstance.setCallback(CANCEL_TRANSACTION, onCancelTransaction)
    checkoutInstance.setCallback(ERROR_EVENT, onErrorEvent)
    checkoutInstance.setCallback(PAYMENT_COMPLETE, onPaymentComplete)
    // create checkout form, a real Moneris Checkout instance present in the browser!
    checkoutInstance.startCheckout(ticketNumber)
    // notify redux store, i'm ready
    dispatch(checkoutReadyAction)
  })

  // TODO: 3. check payment error, and popup message modal
  useEffect(() => {
    const checkoutInstance = monerisRef.current
    if (!checkoutInstance) return
    if (!ticketNumber) return // ticketNumber undefined is invalid, no more actions
    if (pageLoadError) return // checkout instance init failed

    if (getReceiptError) {
      // console.warn('>>> getReceiptError: ' + getReceiptError)
      // destroy the checkout form!
      checkoutInstance.closeCheckout(ticketNumber)
      // Go back to summary page to display payment error(such as payment decline) modal.
      // For normal booking procedure, summary route is valid,
      // but for telephone-booking procedure, summary route is invalid!
      // So, we need to check and diffrentiate them in case of this:
      // https://jira.verbinteractive.com/browse/CTM200-540
      if (summaryRouteCheck.routeIsValid) {
        handleRouteChange(appRoutes.summary.pathname)
      }
      // popup message modal
      openModal({
        type:
          cancelTransactionRouteTarget === appRoutes.account.pathname
            ? modalIds.userBookingCustomErrorModal
            : 'payment-error'
      })
      //clear receipt error immediately, otherwise cause dead loop!!!
      dispatch(clearReceipts())
      // clear ticket for its obsolete
      dispatch(clearTicketNumbers())
    }
  })

  // TODO: 4. finally, it succeed at last! Get receipt success handling
  useEffect(() => {
    const checkoutInstance = monerisRef.current
    if (!checkoutInstance || !ticketNumber) return

    if (getReceiptSuccess && receipt && orderNo) {
      checkoutInstance.closeCheckout(ticketNumber)
      // ticket and orderNo details are wiped out because we only want them saved on success
      // so we'll pass it as a location.state item to
      // be used on the payment confirmation call.
      handleRouteChange(appRoutes.bookingConfirmation.pathname, {
        monerisCheckoutData: {
          orderNo
        }
      })
    }
  }, [
    getReceiptSuccess,
    receipt,
    ticketNumber,
    handleRouteChange,
    dispatch,
    orderNo
  ])

  // reset page load error message!
  useEffect(() => {
    if (preloadReady && !pageLoadError && preloadError) {
      setPageLoadError(labels.paymentConfirmationError)
    }
  }, [preloadReady, pageLoadError, preloadError, labels])

  return preloadError || pageLoadError ? (
    <p className="u-page-view-container u-padding-top u-text-center u-error-color">
      {pageLoadError}
    </p>
  ) : (
    <div id="moneris-frame">
      {checkoutStarted && <BrandLoader />}
      <div>
        <div style={{ height: '845px', width: `${width}px` }}>
          <div id={CHECKOUT_DIV_ID} />
        </div>
      </div>
    </div>
  )
}

MonerisCheckoutFrameWrapper.propTypes = {
  preloadReady: bool.isRequired,
  ticketNumber: string,
  orderNo: string,
  preloadError: bool.isRequired,
  pageData: shape().isRequired,
  labels: shape().isRequired,
  checkoutInstance: shape(),
  openModal: func.isRequired,
  handleRouteChange: func.isRequired,
  cancelTransactionRouteTarget: string
}

export default MonerisCheckoutFrameWrapper
